import { DataTablePrimeReact, TableERP } from "@finanedi.es/storybook"
import { useQuery } from "@tanstack/react-query"
import { useDocumentoComercialPendienteFirmar } from "api/DocumentoComercial.hook"
import { DocumentoComercialValidacionDtoProps } from "api/DTOs/DocumentoComercialValidacionDto"
import { GetAllImpuestosDtoProps } from "api/DTOs/GetAllImpuestosDto"
import { ImpuestoArticuloGetResumenImpuestosFacturaDtoProps } from "api/DTOs/ImpuestoArticuloGetResumenImpuestosFacturaDto"
import { ImpuestoGetResumenImpuestosFacturaDto } from "api/DTOs/ImpuestoGetResumenImpuestosFacturaDto"
import {
  getResumenValidacionImpuestos,
  putFacturaTags,
  validarCuentasContables,
  validarImpuestos,
} from "api/Factura.service"
import { useImpuestoEmpresaNew } from "api/ImpuestoEmpresa.hook"
import { CATEGORIA_CUENTA_CONTABLE_ENUM } from "api/Types/CategoriaCuentaContableEnum"
import { TIPO_GRUPO_IMPUESTO } from "api/Types/TipoGrupoImpuesto"
import { TIPO_IMPUESTO_EMPRESA_ENUM } from "api/Types/TipoImpuestoEmpresaEnum"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import { downloadDocumentoFetch } from "common/components/ModalVerDocumento/constants"
import NativePDF from "common/components/NativePDF/NativePDF"
import NoDataTemplate from "common/components/NoDataTemplate"
import { STATIC_ASSETS_URL } from "common/context/constants"
import FacturaService from "common/services/FacturaService"
import notification from "common/utils/notification"
import useCuentasContables from "features/Tesoreria/hooks/useCuentasContables"
import { useFormik } from "formik"
import { useDispatch } from "hooks/useDispatch"
import { Skeleton } from "primereact/skeleton"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useParams } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import * as Yup from "yup"
import {
  DOCUMENT_TYPE_SERVICE,
  DocumentoValidacion,
  FLOW_STATUS_OPTIONS,
  FormValues,
} from "../constant"
import { DOCUMENTOS_DETALLE_COLUMNS } from "../constants/AprobarDocumentosDetalleColumns"
import AprobarDocumento from "./components/AprobacionDocuments"
import { docPendienteValidacionParams } from "./redux.old"
import "./style.css"

const ValidacionesDetail = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const {
    location: { state },
  } = useHistory()
  const dispatch = useDispatch()
  const params = useParams()
  const [validacionesSelected, setValidacionesSelected] = useState<
    DocumentoComercialValidacionDtoProps[]
  >(
    state.validacionesSelected[0].length > 0
      ? state.validacionesSelected[0]
      : state.validacionesSelected ?? []
  )
  const dtRef =
    useRef<DataTablePrimeReact<DocumentoComercialValidacionDtoProps[]>>(null)
  const { activeCompany } = useUsuarioEmpresaActiva()
  const { documentoComercialRefetch } = useDocumentoComercialPendienteFirmar()

  const { getCuentaContablePorDefecto } = useCuentasContables()
  const [validaciones, setValidaciones] = useState(
    state.validacionesSelected[0].length > 0
      ? state.validacionesSelected[0]
      : state.validacionesSelected ?? []
  )
  const [rowSelected, setRowSelected] = useState(params.id ?? undefined)
  const [isValidating, setIsValidating] = useState(false)

  const initialPorDefectoImpuestosVentas = getCuentaContablePorDefecto(
    CATEGORIA_CUENTA_CONTABLE_ENUM.IVA_VENTA
  )
  const initialPorDefectoCompras = getCuentaContablePorDefecto(
    CATEGORIA_CUENTA_CONTABLE_ENUM.COMPRA
  )
  const initialPorDefectoRetencionesVentas = getCuentaContablePorDefecto(
    CATEGORIA_CUENTA_CONTABLE_ENUM.RETENCION_VENTA
  )
  const { impuestoEmpresa: tipoImpuestos } = useImpuestoEmpresaNew({
    TipoImpuestoEmpresa: TIPO_IMPUESTO_EMPRESA_ENUM.COMPRA,
    TipoGrupoImpuesto: TIPO_GRUPO_IMPUESTO.IMPUESTO_REPERCUTIDO,
    IsPagingEnabled: false,
  })

  const handleHide = (): void => {
    history.push({
      pathname: ROUTES.VALIDACIONES_DOCUMENTOS,
      state,
    })
  }
  const [tabSelected, setTabSelected] = useState(0)

  const validationSchema = Yup.object().shape({
    lastSignature: Yup.boolean(),
    cuentaContableId: Yup.string().when("lastSignature", {
      is: true,
      then: Yup.string()
        .nullable()
        .required("Campo obligatorio")
        .min(1, "Campo obligatorio"),
    }),
    fecha: Yup.date().when("lastSignature", {
      is: true,
      then: Yup.date().required("La fecha es requerida"),
    }),
    impuestoEmpresaId: Yup.string()
      .nullable(true)
      .test(
        "impuestoEmpresaId-validation",
        "Campo obligatorio", // Mensaje de error
        (value, context) => {
          const { empresaCreadoraId } = context.parent // Accede a los valores del objeto actual
          if (empresaCreadoraId && activeCompany.id !== empresaCreadoraId) {
            return true // Permitir `null` si activeCompanyId no coincide con empresaCreadoraId
          }
          return value !== null && value !== "" // Validar que no sea `null` ni vacío
        }
      ),
    retenciones:
      tabSelected === 1
        ? Yup.array().of(
            Yup.object().shape({
              cuentaContableId: Yup.string()
                .required("Campo obligatorio")
                .min(1, "Campo obligatorio"),
            })
          )
        : Yup.array().notRequired(),
  })

  const formik = useFormik<FormValues>({
    initialValues: {
      lastSignature: undefined,
      cuentaContableId: "",
      importe: 0,
      impuestos: [],
      listadoTags: [],
      retenciones: [],
      subtotal: 0,
      comentario: "",
      fecha: "",
      estadoRevision: FLOW_STATUS_OPTIONS[0].value,
      empresaCreadoraId: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (tabSelected === 0) {
        setTabSelected(1)
        return
      }
      if (tabSelected === 1 && values.retenciones.length > 0) {
        setTabSelected(2)
        return
      }
      if (
        validacionesSelected.length === 1 &&
        validacionesSelected[0].tipoDocumento === 2 &&
        values.lastSignature
      ) {
        try {
          if (!validacionesSelected[0].id) return
          await putFacturaTags({
            id: validacionesSelected[0].id,
            tags: values.listadoTags,
          })
          const dataCuentasContables = await validarCuentasContables(
            validacionesSelected[0].id,
            {
              id: validacionesSelected[0].id,
              categoriaLibradoId: values.cuentaContableId,
              fechaContabilizacion: values.fecha,
              impuestoRetenidoCuentaContableId:
                values?.retenciones[0]?.cuentaContableId || null,
              impuestos:
                values.impuestos.map((impuesto) => {
                  return {
                    cuentaContableId: impuesto.cuentaContableId,
                    porcentaje: impuesto.porcentaje ?? 0,
                  }
                }) || null,
            }
          )

          if (activeCompany.id === values.empresaCreadoraId) {
            await validarImpuestos(validacionesSelected[0].id, {
              id: validacionesSelected[0].id,
              impuestos:
                values.impuestos.map((impuesto) => {
                  return {
                    articuloId: impuesto.articuloId,
                    impuestoEmpresaId: impuesto.impuestoEmpresaId as string,
                  }
                }) || null,
            })
          }
          if (dataCuentasContables === 200) {
            await FacturaService.validateDocumentoPendiente(
              validacionesSelected[0].id,
              {
                id: validacionesSelected[0].id,
                comentario: values.comentario,
                estadoRevision: values.estadoRevision,
              }
            )
              .then((resp) => {
                resp.status === 200 && handleHide()
                resp.status === 200 &&
                  notification.success({
                    body: `${t("validaciones.documentos.detail.msgCorrect")}`,
                  })
                documentoComercialRefetch()
              })
              .catch((error) => {
                // eslint-disable-next-line no-console
                console.error(error)
              })
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error)
        }
      } else {
        await Promise.all(
          validacionesSelected.map(async ({ id }) => {
            setIsValidating(true)
            await DOCUMENT_TYPE_SERVICE[state.docType].service
              .validateDocumentoPendiente(id, {
                id,
                comentario: values.comentario,
                estadoRevision: values.estadoRevision,
              })
              .then(() => {
                setValidacionesSelected(
                  validacionesSelected.filter(
                    (item: DocumentoComercialValidacionDtoProps) =>
                      item.id !== id
                  )
                )
                setValidaciones((validaciones: $TSMigrationFixMe) => {
                  const filteredValidaciones = validaciones.filter(
                    (item: DocumentoValidacion) => item.id !== id
                  )
                  if (filteredValidaciones.length === 0) {
                    handleHide()
                  }
                  return filteredValidaciones
                })
                documentoComercialRefetch()
                notification.success({
                  body: `${t("validaciones.documentos.detail.msgCorrect")}`,
                })
              })
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .catch((error: $TSMigrationFixMe) => {
                // eslint-disable-next-line no-console
                console.error(error)
              })
            setIsValidating(false)
          })
        )
      }
    },
  })

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
        e.preventDefault()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
        e.preventDefault()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  const { data: pdfDocument, isLoading: pdfDocumentIsLoading } = useQuery({
    queryKey: ["pdf", rowSelected, state.docType],
    enabled: Boolean(rowSelected),
    queryFn: () => {
      if (!rowSelected) return
      return downloadDocumentoFetch(state.docType, rowSelected)
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      return (
        failureCount < 10 && error.message !== "Tipo de documento no soportado"
      )
    },
    retryDelay: 2000,
    retryOnMount: false,
    refetchOnMount: "always",
  })

  const fetchData = async () => {
    if (rowSelected) {
      docPendienteValidacionParams({
        dispatch,
        service:
          DOCUMENT_TYPE_SERVICE[state.docType].service
            .getDocumentoPendienteValidacion,
        id: rowSelected,
      })

      const lastSignature: boolean =
        state.docType === 2 &&
        validacionesSelected.length === 1 &&
        (validacionesSelected[0]?.numFirmas ?? 0) -
          (validacionesSelected[0]?.numFirmados ?? 0) ===
          1

      const resumenData = lastSignature
        ? await getResumenValidacionImpuestos(rowSelected)
        : null

      formik.setValues({
        lastSignature,
        cuentaContableId:
          resumenData?.cuentaContableId ?? initialPorDefectoCompras,
        importe: resumenData?.importe ?? 0,
        empresaCreadoraId: resumenData?.empresaCreadoraId ?? null,
        impuestos:
          resumenData?.impuestos?.map(
            ({
              articuloId,
              clave,
              impuesto,
            }: ImpuestoArticuloGetResumenImpuestosFacturaDtoProps) => {
              return {
                cuentaContableId:
                  impuesto?.cuentaContableId ??
                  initialPorDefectoImpuestosVentas,
                articuloId: articuloId ?? null,
                impuestoEmpresaId:
                  tipoImpuestos?.items?.filter(
                    (impuestoT: GetAllImpuestosDtoProps) =>
                      clave
                        ? impuestoT.clave === clave
                        : impuestoT.valor === impuesto?.porcentaje
                  )[0]?.id ?? null,
                importe: impuesto?.importe ?? 0,
                porcentaje: impuesto?.porcentaje ?? 0,
                subtotal: impuesto?.subtotal ?? 0,
              }
            }
          ) ?? [],
        listadoTags: resumenData?.listadoTags ?? [],
        retenciones:
          resumenData?.retenciones?.map(
            ({
              cuentaContableId,
              importe,
              porcentaje,
              subtotal,
            }: ImpuestoGetResumenImpuestosFacturaDto) => {
              return {
                cuentaContableId:
                  cuentaContableId ?? initialPorDefectoRetencionesVentas,
                importe: importe ?? 0,
                porcentaje: porcentaje ?? 0,
                subtotal: subtotal ?? 0,
              }
            }
          ) ?? [],
        subtotal: resumenData?.subtotal ?? 0,
        comentario: "",
        fecha:
          resumenData?.fechaContabilizacion ??
          new Date().toISOString().split("T")[0],
        estadoRevision: 2,
      })
    }
  }

  useEffect(() => {
    const loadDependenciesAndFetch = async () => {
      if (!rowSelected || !tipoImpuestos) return

      await Promise.all([rowSelected, tipoImpuestos])
      await fetchData()
    }

    loadDependenciesAndFetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelected, tipoImpuestos])

  return (
    <div className='tw-overflow-hidden tw-h-full tw-flex tw-flex-row'>
      <div className='tw-w-4/6 tw-h-full'>
        <div className='tw-h-1/2'>
          {pdfDocumentIsLoading ? (
            <Skeleton className='tw-w-full !tw-h-full' />
          ) : (
            <NativePDF
              pdf={pdfDocument}
              type='stream'
            />
          )}
        </div>
        <div className='tw-h-1/2'>
          <TableERP
            refTable={dtRef}
            columns={DOCUMENTOS_DETALLE_COLUMNS(t, setRowSelected)}
            className='tw-h-full'
            dataKey='id'
            columnResizeMode='expand'
            removableSort
            value={validaciones}
            selection={validacionesSelected}
            selectionMode='checkbox'
            size='small'
            selectionColumnMode='multiple'
            onSelectionChange={({
              value,
            }: {
              originalEvent: React.SyntheticEvent
              value: DocumentoComercialValidacionDtoProps[]
              type?: "multiple" | "all" | "checkbox" | "row" | undefined
            }) => {
              setValidacionesSelected(value)
            }}
            showGridlines
            lazy
            scrollable
            emptyMessage={
              <NoDataTemplate
                icon={`${STATIC_ASSETS_URL}/Imagenes/Ilustraciones/tablas/facturas-de-venta.svg`}
                title='nsProformaInvoices.header.title'
                description='shared.noDataDescription'
                actionLabel='nsProformaInvoices.header.newInvoice'
                showActions={false}
              />
            }
          />
        </div>
      </div>
      <div className='tw-w-2/6 tw-h-full'>
        <AprobarDocumento
          formik={formik}
          tabSelected={tabSelected}
          setTabSelected={setTabSelected}
          validacionesSelected={validacionesSelected}
          isValidating={isValidating}
          handleHide={handleHide}
          tipoImpuestos={tipoImpuestos?.items}
        />
      </div>
    </div>
  )
}

export default ValidacionesDetail
